import React, { useState } from 'react';
import { 
  heroData, 
  features, 
  howItWorks, 
  documentTypes, 
  stats, 
  testimonials, 
  faqData,
  contactInfo,
  logoUrl,
  complianceData,
  pricingPlans
} from '@/data/mock';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { 
  Focus, 
  FileText, 
  Heart, 
  Shield, 
  Clock, 
  Users,
  Mic,
  Sparkles,
  FileCheck,
  CheckCircle2,
  ArrowRight,
  Menu,
  X,
  Mail,
  Phone,
  MapPin,
  ChevronRight,
  Globe,
  Lock,
  Server,
  Eye,
  BadgeCheck,
  Check,
  Zap
} from 'lucide-react';

const iconMap = {
  Focus: Focus,
  FileText: FileText,
  Heart: Heart,
  Shield: Shield,
  Clock: Clock,
  Users: Users
};

const complianceIconMap = {
  Globe: Globe,
  Lock: Lock,
  Clock: Clock,
  Shield: Shield,
  Server: Server,
  Users: Users
};

// Construction Banner Component
const ConstructionBanner = () => {
  return (
    <div className="fixed top-0 left-0 right-0 z-[60] bg-slate-900/90 backdrop-blur-sm text-white py-3 px-4">
      <div className="max-w-7xl mx-auto flex items-center justify-center gap-3">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"></div>
          <span className="text-sm md:text-base font-medium">
            🚧 <span className="text-orange-400 font-semibold">New Product</span> - Site Under Construction
          </span>
          <div className="w-2 h-2 bg-orange-400 rounded-full animate-pulse"></div>
        </div>
      </div>
    </div>
  );
};

// Header Component
const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="fixed top-10 left-0 right-0 z-50 bg-white/90 backdrop-blur-md border-b border-slate-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <img src={logoUrl} alt="Present Mind App" className="h-10 w-10 md:h-12 md:w-12 rounded-lg" />
            <span className="font-semibold text-lg md:text-xl text-slate-800">Present Mind App</span>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-8">
            <a href="#features" className="text-slate-600 hover:text-slate-900 transition-colors">Features</a>
            <a href="#pricing" className="text-slate-600 hover:text-slate-900 transition-colors">Pricing</a>
            <a href="#how-it-works" className="text-slate-600 hover:text-slate-900 transition-colors">How It Works</a>
            <a href="#compliance" className="text-slate-600 hover:text-slate-900 transition-colors">Compliance</a>
            <a href="#faq" className="text-slate-600 hover:text-slate-900 transition-colors">FAQ</a>
            <a href="#contact" className="text-slate-600 hover:text-slate-900 transition-colors">Contact</a>
          </nav>

          {/* CTA Button */}
          <div className="hidden md:block">
            <a href="#contact">
              <Button className="bg-orange-500 hover:bg-orange-600 text-white px-6">
                Contact Us
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-100">
            <nav className="flex flex-col gap-4">
              <a href="#features" className="text-slate-600 hover:text-slate-900 transition-colors" onClick={() => setMobileMenuOpen(false)}>Features</a>
              <a href="#pricing" className="text-slate-600 hover:text-slate-900 transition-colors" onClick={() => setMobileMenuOpen(false)}>Pricing</a>
              <a href="#how-it-works" className="text-slate-600 hover:text-slate-900 transition-colors" onClick={() => setMobileMenuOpen(false)}>How It Works</a>
              <a href="#compliance" className="text-slate-600 hover:text-slate-900 transition-colors" onClick={() => setMobileMenuOpen(false)}>Compliance</a>
              <a href="#faq" className="text-slate-600 hover:text-slate-900 transition-colors" onClick={() => setMobileMenuOpen(false)}>FAQ</a>
              <a href="#contact" className="text-slate-600 hover:text-slate-900 transition-colors" onClick={() => setMobileMenuOpen(false)}>Contact</a>
              <a href="#contact" onClick={() => setMobileMenuOpen(false)}>
                <Button className="bg-orange-500 hover:bg-orange-600 text-white w-full mt-2">
                  Contact Us
                </Button>
              </a>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="pt-36 md:pt-44 pb-16 md:pb-24 bg-gradient-to-b from-slate-50 to-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Left Content */}
          <div className="text-left">
            <div className="inline-flex items-center gap-2 bg-orange-50 text-orange-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Sparkles className="h-4 w-4" />
              {heroData.tagline}
            </div>
            
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 leading-tight mb-6">
              {heroData.headline}
            </h1>
            
            <p className="text-lg md:text-xl text-slate-600 mb-8 max-w-xl">
              {heroData.subheadline}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-6 text-lg">
                {heroData.ctaPrimary}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
              <Button size="lg" variant="outline" className="border-slate-300 text-slate-700 hover:bg-slate-50 px-8 py-6 text-lg">
                {heroData.ctaSecondary}
              </Button>
            </div>

            {/* Trust Badges */}
            <div className="flex items-center gap-6 mt-10 pt-10 border-t border-slate-200">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-green-500" />
                <span className="text-sm text-slate-600">GDPR Compliant</span>
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-green-500" />
                <span className="text-sm text-slate-600">UK Based</span>
              </div>
            </div>
          </div>

          {/* Right - App Preview */}
          <div className="relative lg:pl-8">
            <div className="relative">
              {/* Decorative Elements */}
              <div className="absolute -top-4 -left-4 w-72 h-72 bg-orange-100 rounded-full blur-3xl opacity-50"></div>
              <div className="absolute -bottom-4 -right-4 w-72 h-72 bg-blue-100 rounded-full blur-3xl opacity-50"></div>
              
              {/* App Card */}
              <div className="relative bg-white rounded-2xl shadow-2xl border border-slate-200 p-6 md:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <img src={logoUrl} alt="Present Mind" className="h-12 w-12 rounded-lg" />
                  <div>
                    <h3 className="font-semibold text-slate-800">Present Mind App</h3>
                    <p className="text-sm text-slate-500">Recording in progress...</p>
                  </div>
                </div>
                
                {/* Recording UI Mock */}
                <div className="bg-slate-900 rounded-xl p-8 text-center">
                  <div className="relative w-40 h-40 mx-auto mb-4">
                    <div className="absolute inset-0 rounded-full border-4 border-blue-500/30 animate-pulse"></div>
                    <div className="absolute inset-4 rounded-full border-4 border-blue-500/50 animate-pulse" style={{animationDelay: '0.2s'}}></div>
                    <div className="absolute inset-8 rounded-full border-4 border-blue-500/70 animate-pulse" style={{animationDelay: '0.4s'}}></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <div className="text-center">
                        <p className="text-3xl font-mono text-white font-bold">03:24</p>
                        <p className="text-blue-400 text-sm mt-1">Being Present...</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex justify-center gap-4">
                    <Button variant="outline" size="sm" className="border-slate-600 text-slate-300 hover:bg-slate-800">
                      Pause
                    </Button>
                    <Button size="sm" className="bg-red-500 hover:bg-red-600 text-white">
                      Stop Recording
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Stats Section
const StatsSection = () => {
  return (
    <section className="py-12 bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
          {stats.map((stat, index) => (
            <div key={index} className="text-center">
              <p className="text-3xl md:text-4xl font-bold text-orange-400 mb-2">{stat.value}</p>
              <p className="text-slate-400 text-sm md:text-base">{stat.label}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Features Section
const FeaturesSection = () => {
  return (
    <section id="features" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Everything You Need with Present Mind App
          </h2>
          <p className="text-lg text-slate-600">
            Powerful features designed for frontline housing professionals who deal with complex situations and sensitive information.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {features.map((feature) => {
            const IconComponent = iconMap[feature.icon];
            return (
              <Card key={feature.id} className="border-slate-200 hover:border-orange-200 hover:shadow-lg transition-all duration-300 group">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-orange-50 rounded-lg flex items-center justify-center mb-4 group-hover:bg-orange-100 transition-colors">
                    <IconComponent className="h-6 w-6 text-orange-500" />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900 mb-2">{feature.title}</h3>
                  <p className="text-slate-600">{feature.description}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = () => {
  return (
    <section id="pricing" className="py-20 md:py-28 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 bg-orange-50 text-orange-600 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Zap className="h-4 w-4" />
            Simple Pricing
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Choose Your Plan
          </h2>
          <p className="text-lg text-slate-600">
            Flexible weekly subscriptions. Pay for what you need, cancel anytime.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {pricingPlans.map((plan, index) => (
            <Card 
              key={index} 
              className={`relative border-2 transition-all duration-300 hover:shadow-xl ${
                plan.highlighted 
                  ? 'border-orange-500 bg-white shadow-lg scale-105' 
                  : 'border-slate-200 bg-white hover:border-orange-200'
              }`}
            >
              {plan.badge && (
                <div className={`absolute -top-3 left-1/2 transform -translate-x-1/2 px-4 py-1 rounded-full text-xs font-semibold ${
                  plan.highlighted 
                    ? 'bg-orange-500 text-white' 
                    : plan.tier === 'Free' 
                      ? 'bg-green-500 text-white'
                      : 'bg-slate-700 text-white'
                }`}>
                  {plan.badge}
                </div>
              )}
              
              <CardContent className="p-6 pt-8">
                <div className="text-center mb-6">
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{plan.tier}</h3>
                  <div className="flex items-baseline justify-center gap-1">
                    <span className="text-4xl font-bold text-slate-900">{plan.price}</span>
                    {plan.billing !== 'One-time' && (
                      <span className="text-slate-500 text-sm">/{plan.billing.toLowerCase()}</span>
                    )}
                  </div>
                  <p className="text-sm text-slate-500 mt-2">{plan.description}</p>
                </div>

                <div className="bg-slate-50 rounded-lg p-4 mb-6">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-600">Recording Time</span>
                    <span className="font-semibold text-slate-900">{plan.time}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-600">Est. Meetings</span>
                    <span className="font-semibold text-slate-900">{plan.meetings}</span>
                  </div>
                </div>

                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                      <span className="text-sm text-slate-600">{feature}</span>
                    </li>
                  ))}
                </ul>

                <a href="#contact">
                  <Button 
                    className={`w-full ${
                      plan.highlighted 
                        ? 'bg-orange-500 hover:bg-orange-600 text-white' 
                        : 'bg-slate-900 hover:bg-slate-800 text-white'
                    }`}
                  >
                    {plan.cta}
                  </Button>
                </a>
              </CardContent>
            </Card>
          ))}
        </div>

        <p className="text-center text-sm text-slate-500 mt-8">
          All plans include GDPR compliance, 3-day auto data deletion, and UK-based support.
        </p>
      </div>
    </section>
  );
};

// How It Works Section
const HowItWorksSection = () => {
  const stepIcons = [Mic, Sparkles, FileText, FileCheck];
  
  return (
    <section id="how-it-works" className="py-20 md:py-28 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            How Present Mind App Works
          </h2>
          <p className="text-lg text-slate-600">
            From recording to documentation in four simple steps.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {howItWorks.map((item, index) => {
            const StepIcon = stepIcons[index];
            return (
              <div key={item.step} className="relative">
                {/* Connector Line */}
                {index < howItWorks.length - 1 && (
                  <div className="hidden lg:block absolute top-12 left-1/2 w-full h-0.5 bg-slate-200">
                    <ChevronRight className="absolute -right-3 -top-2 h-5 w-5 text-slate-300" />
                  </div>
                )}
                
                <div className="relative bg-white rounded-2xl p-6 shadow-sm border border-slate-200 hover:shadow-md transition-shadow">
                  <div className="w-14 h-14 bg-slate-900 rounded-xl flex items-center justify-center mb-4 relative">
                    <StepIcon className="h-7 w-7 text-white" />
                    <span className="absolute -top-2 -right-2 w-6 h-6 bg-orange-500 rounded-full flex items-center justify-center text-white text-sm font-bold">
                      {item.step}
                    </span>
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">{item.title}</h3>
                  <p className="text-slate-600 text-sm">{item.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// Document Types Section
const DocumentTypesSection = () => {
  return (
    <section className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Generate Professional Documents Instantly
            </h2>
            <p className="text-lg text-slate-600 mb-8">
              Present Mind App automatically populates your organisation's template documents with information from your conversations. No more writing up file notes later or manually logging communications on housing systems.
            </p>
            
            <div className="grid grid-cols-2 gap-3">
              {documentTypes.map((doc, index) => (
                <div key={index} className="flex items-center gap-2 text-slate-700">
                  <CheckCircle2 className="h-5 w-5 text-green-500 flex-shrink-0" />
                  <span>{doc}</span>
                </div>
              ))}
            </div>
          </div>
          
          <div className="relative">
            <div className="absolute inset-0 bg-gradient-to-br from-orange-100 to-blue-100 rounded-3xl transform rotate-3"></div>
            <div className="relative bg-white rounded-2xl shadow-xl p-6 border border-slate-200">
              <div className="flex items-center gap-2 mb-4">
                <FileText className="h-5 w-5 text-orange-500" />
                <span className="font-medium text-slate-800">Risk Assessment</span>
                <span className="ml-auto text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">Generated</span>
              </div>
              <div className="space-y-3">
                <div className="h-3 bg-slate-100 rounded w-full"></div>
                <div className="h-3 bg-slate-100 rounded w-4/5"></div>
                <div className="h-3 bg-slate-100 rounded w-3/4"></div>
                <div className="h-8 bg-slate-50 rounded mt-4"></div>
                <div className="h-3 bg-slate-100 rounded w-full"></div>
                <div className="h-3 bg-slate-100 rounded w-5/6"></div>
              </div>
              <div className="flex gap-2 mt-6">
                <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white">Download PDF</Button>
                <Button size="sm" variant="outline">Edit</Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// Compliance Section
const ComplianceSection = () => {
  return (
    <section id="compliance" className="py-20 md:py-28 bg-slate-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-full text-sm font-medium mb-6">
            <BadgeCheck className="h-4 w-4" />
            Compliance Library
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            {complianceData.headline}
          </h2>
          <p className="text-lg text-slate-600">
            {complianceData.subheadline}
          </p>
        </div>

        {/* At a Glance Cards */}
        <div className="mb-12">
          <div className="flex items-center gap-2 mb-6">
            <Eye className="h-5 w-5 text-slate-700" />
            <h3 className="text-xl font-semibold text-slate-900">At a Glance</h3>
          </div>
          <p className="text-slate-600 mb-6">Key compliance facts about Present Mind App</p>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {complianceData.atAGlance.map((item, index) => {
              const IconComponent = complianceIconMap[item.icon];
              return (
                <Card key={index} className="border-slate-200 bg-white">
                  <CardContent className="p-6 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-slate-100 flex items-center justify-center">
                      <IconComponent className="h-6 w-6 text-slate-600" />
                    </div>
                    <p className="text-sm text-slate-500 mb-1">{item.label}</p>
                    <p className="font-semibold text-slate-900">{item.value}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>

        {/* Compliance Categories */}
        <div className="flex flex-wrap justify-center gap-3 mb-12">
          {complianceData.categories.map((category, index) => (
            <span 
              key={index}
              className="px-4 py-2 bg-white border border-slate-200 rounded-full text-sm text-slate-600 hover:border-orange-300 hover:text-orange-600 transition-colors cursor-pointer"
            >
              {category}
            </span>
          ))}
        </div>

        {/* Documentation Cards */}
        <div className="grid md:grid-cols-2 gap-6">
          {complianceData.documents.map((doc, index) => (
            <Card key={index} className="border-slate-200 bg-white hover:shadow-md transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center flex-shrink-0">
                    <FileText className="h-5 w-5 text-orange-600" />
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-slate-900 mb-1">{doc.title}</h4>
                    <p className="text-sm text-slate-500 mb-3">Updated: {doc.updated}</p>
                    <div className="flex gap-2">
                      <Button size="sm" variant="outline" className="text-slate-600">
                        <Eye className="h-4 w-4 mr-1" />
                        View
                      </Button>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const TestimonialsSection = () => {
  return (
    <section id="testimonials" className="py-20 md:py-28 bg-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Trusted by Frontline Housing Teams
          </h2>
          <p className="text-lg text-slate-400">
            See how Present Mind App is helping support workers, neighbourhood officers, and housing teams across the UK.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8">
          {testimonials.map((testimonial) => (
            <Card key={testimonial.id} className="bg-slate-800 border-slate-700">
              <CardContent className="p-6">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <svg key={i} className="w-5 h-5 text-orange-400 fill-current" viewBox="0 0 20 20">
                      <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z"/>
                    </svg>
                  ))}
                </div>
                <p className="text-slate-300 mb-6 italic">"{testimonial.quote}"</p>
                <div className="border-t border-slate-700 pt-4">
                  <p className="font-medium text-white">{testimonial.author}</p>
                  <p className="text-sm text-slate-400">{testimonial.organisation}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
};

// FAQ Section
const FAQSection = () => {
  return (
    <section id="faq" className="py-20 md:py-28 bg-white">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-lg text-slate-600">
            Everything you need to know about Present Mind App.
          </p>
        </div>

        <Accordion type="single" collapsible className="space-y-4">
          {faqData.map((faq, index) => (
            <AccordionItem 
              key={index} 
              value={`item-${index}`}
              className="border border-slate-200 rounded-lg px-6 data-[state=open]:border-orange-200 data-[state=open]:bg-orange-50/50 transition-colors"
            >
              <AccordionTrigger className="text-left font-medium text-slate-900 hover:text-orange-600 py-4">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 pb-4">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
};

// CTA Section
const CTASection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organisation: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    // Placeholder - would submit to backend
    setSubmitted(true);
  };

  return (
    <section className="py-20 md:py-28 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              Ready to Transform How Your Team Works?
            </h2>
            <p className="text-lg text-slate-300 mb-8">
              Join housing associations and local authorities across the UK who are using Present Mind App to help frontline staff focus on clients, not paperwork.
            </p>
            
            <div className="space-y-4">
              <div className="flex items-center gap-3 text-slate-300">
                <CheckCircle2 className="h-5 w-5 text-green-400" />
                <span>Free demo and consultation</span>
              </div>
              <div className="flex items-center gap-3 text-slate-300">
                <CheckCircle2 className="h-5 w-5 text-green-400" />
                <span>Custom template configuration</span>
              </div>
              <div className="flex items-center gap-3 text-slate-300">
                <CheckCircle2 className="h-5 w-5 text-green-400" />
                <span>Dedicated onboarding support</span>
              </div>
              <div className="flex items-center gap-3 text-slate-300">
                <CheckCircle2 className="h-5 w-5 text-green-400" />
                <span>UK-based team and support</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 md:p-8 shadow-xl">
            {submitted ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="h-8 w-8 text-green-500" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Thank You!</h3>
                <p className="text-slate-600">We'll be in touch shortly to arrange your demo.</p>
              </div>
            ) : (
              <>
                <h3 className="text-xl font-semibold text-slate-900 mb-6">Request a Demo</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Input
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      className="border-slate-300"
                    />
                  </div>
                  <div>
                    <Input
                      type="email"
                      placeholder="Work Email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      required
                      className="border-slate-300"
                    />
                  </div>
                  <div>
                    <Input
                      placeholder="Organisation"
                      value={formData.organisation}
                      onChange={(e) => setFormData({...formData, organisation: e.target.value})}
                      required
                      className="border-slate-300"
                    />
                  </div>
                  <div>
                    <Textarea
                      placeholder="Tell us about your needs (optional)"
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      rows={3}
                      className="border-slate-300"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600 text-white py-6">
                    Request Demo
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Contact Section
const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    organisation: '',
    subject: '',
    message: ''
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await fetch('/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        setSubmitted(true);
      }
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="py-20 md:py-28 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Get In Touch
          </h2>
          <p className="text-lg text-slate-600">
            Have questions about Present Mind App? We'd love to hear from you.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Information */}
          <div>
            <h3 className="text-2xl font-semibold text-slate-900 mb-6">Contact Information</h3>
            <p className="text-slate-600 mb-8">
              Reach out to us directly or fill in the enquiry form and we'll get back to you as soon as possible.
            </p>
            
            <div className="space-y-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Mail className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-1">Email Us</h4>
                  <a 
                    href="mailto:support@presentmindapp.co.uk" 
                    className="text-orange-600 hover:text-orange-700 transition-colors"
                  >
                    support@presentmindapp.co.uk
                  </a>
                  <p className="text-sm text-slate-500 mt-1">We typically respond within 24 hours</p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <MapPin className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900 mb-1">Location</h4>
                  <p className="text-slate-600">United Kingdom</p>
                  <p className="text-sm text-slate-500 mt-1">UK-based team and support</p>
                </div>
              </div>
            </div>

            {/* Trust badges */}
            <div className="mt-10 pt-10 border-t border-slate-200">
              <p className="text-sm text-slate-500 mb-4">Trusted & Secure</p>
              <div className="flex flex-wrap gap-4">
                <div className="flex items-center gap-2 bg-slate-100 px-3 py-2 rounded-lg">
                  <Shield className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-slate-700">GDPR Compliant</span>
                </div>
                <div className="flex items-center gap-2 bg-slate-100 px-3 py-2 rounded-lg">
                  <BadgeCheck className="h-4 w-4 text-green-600" />
                  <span className="text-sm text-slate-700">Cyber Essentials Plus</span>
                </div>
              </div>
            </div>
          </div>

          {/* Enquiry Form */}
          <div className="bg-slate-50 rounded-2xl p-6 md:p-8">
            {submitted ? (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 className="h-8 w-8 text-green-500" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2">Message Sent!</h3>
                <p className="text-slate-600">Thank you for your enquiry. We'll be in touch shortly.</p>
              </div>
            ) : (
              <>
                <h3 className="text-xl font-semibold text-slate-900 mb-6">Send Us a Message</h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                      <Input
                        placeholder="Your name"
                        value={formData.name}
                        onChange={(e) => setFormData({...formData, name: e.target.value})}
                        required
                        className="border-slate-300 bg-white"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                      <Input
                        type="email"
                        placeholder="your.email@organisation.co.uk"
                        value={formData.email}
                        onChange={(e) => setFormData({...formData, email: e.target.value})}
                        required
                        className="border-slate-300 bg-white"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Organisation</label>
                    <Input
                      placeholder="Your organisation name"
                      value={formData.organisation}
                      onChange={(e) => setFormData({...formData, organisation: e.target.value})}
                      className="border-slate-300 bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Subject *</label>
                    <Input
                      placeholder="What is your enquiry about?"
                      value={formData.subject}
                      onChange={(e) => setFormData({...formData, subject: e.target.value})}
                      required
                      className="border-slate-300 bg-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-1">Message *</label>
                    <Textarea
                      placeholder="Tell us more about your enquiry..."
                      value={formData.message}
                      onChange={(e) => setFormData({...formData, message: e.target.value})}
                      rows={5}
                      required
                      className="border-slate-300 bg-white"
                    />
                  </div>
                  <Button 
                    type="submit" 
                    className="w-full bg-orange-500 hover:bg-orange-600 text-white py-6"
                    disabled={loading}
                  >
                    {loading ? 'Sending...' : 'Send Message'}
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                  <p className="text-xs text-slate-500 text-center mt-4">
                    By submitting this form, you agree to our privacy policy. We'll only use your information to respond to your enquiry.
                  </p>
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="bg-slate-950 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          {/* Brand */}
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <img src={logoUrl} alt="Present Mind App" className="h-10 w-10 rounded-lg" />
              <span className="font-semibold text-xl text-white">Present Mind App</span>
            </div>
            <p className="text-slate-400 mb-4 max-w-md">
              Quality time with every conversation. AI-powered meeting assistant for frontline housing staff.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-white mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li><a href="#features" className="text-slate-400 hover:text-white transition-colors">Features</a></li>
              <li><a href="#how-it-works" className="text-slate-400 hover:text-white transition-colors">How It Works</a></li>
              <li><a href="#compliance" className="text-slate-400 hover:text-white transition-colors">Compliance</a></li>
              <li><a href="#faq" className="text-slate-400 hover:text-white transition-colors">FAQ</a></li>
              <li><a href="#contact" className="text-slate-400 hover:text-white transition-colors">Contact</a></li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="font-semibold text-white mb-4">Contact</h4>
            <ul className="space-y-3">
              <li className="flex items-center gap-2">
                <Mail className="h-4 w-4 text-slate-400" />
                <a href="mailto:support@presentmindapp.co.uk" className="text-slate-400 hover:text-white transition-colors">
                  support@presentmindapp.co.uk
                </a>
              </li>
              <li className="flex items-center gap-2 text-slate-400">
                <MapPin className="h-4 w-4" />
                <span>{contactInfo.address}</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="border-t border-slate-800 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-slate-500 text-sm">
            © {new Date().getFullYear()} Present Mind App. All rights reserved.
          </p>
          <div className="flex gap-6">
            <a href="#" className="text-slate-500 hover:text-white text-sm transition-colors">Privacy Policy</a>
            <a href="#" className="text-slate-500 hover:text-white text-sm transition-colors">Terms of Service</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main Landing Page Component
const LandingPage = () => {
  return (
    <div className="min-h-screen bg-white">
      <ConstructionBanner />
      <Header />
      <main>
        <HeroSection />
        <StatsSection />
        <FeaturesSection />
        <PricingSection />
        <HowItWorksSection />
        <DocumentTypesSection />
        <ComplianceSection />
        <TestimonialsSection />
        <FAQSection />
        <CTASection />
        <ContactSection />
      </main>
      <Footer />
    </div>
  );
};

export default LandingPage;
